<template>
  <div></div>
</template>

<script>
// we do not want these properties to be reactive at all
// update the reactive properties at seperate intervals for performance
let devicesMapHolding = {}
let vehicleStatusHolding = {}

import { getToken, SubscribeVehicleUpdates, UnsubscribeVehicleUpdates } from "@/services/API.js"
import { mapState, mapMutations, mapGetters } from "vuex"
import { getCoor, getColor, getStatus, isUnknown } from "@/utils"
import Sockette from "sockette"
import { Logger } from "aws-amplify"
const logger = new Logger("WEBSOCKET INFO")

export default {
  data () {
    return {
      restartFunc: false,
      restartInterval: false,
      connectionID: false,
      socket: false,
      socketSubscriptions: [],
      socketEventHandlers: {},
      hasSubscription: false
    }
  },

  computed: {
    ...mapState("devicePresence", [
       "vehicles", 
       "devicesMap",
       "vehicleStatus"
    ]),

    ...mapGetters("devicePresence", [
      "vehiclesSet"
    ]),
  },

  mounted () {
    this.addDefaulthandlers()
    this.initWS()

    // this interval checks if a restart function has been created;
    // this may be made if the socket is closed due to error, timeout, or some other reason
    this.restartInterval = setInterval(() => {
      logger.debug("checking for restart function")
      if(this.restartFunc) {
        logger.debug("restart found!")
        this.restartFunc()
        this.restartFunc = false
      } else {
        logger.debug("no restart function")
      }
    }, 30000)

    // this handler is where we set up the store and subscribe to socket events for the selected vehicles
    window.EventBus.$on('initializeMapData', async (orgID, vehicleIDList) => {
      logger.debug('init map data called', orgID, vehicleIDList)

      // we should not try to initiate presence for an org with no vehicles
      if(!Array.isArray(vehicleIDList) || !vehicleIDList.length) {
        return
      }

      devicesMapHolding = JSON.parse(JSON.stringify(this.devicesMap))
      vehicleStatusHolding = JSON.parse(JSON.stringify(this.vehicleStatus))

      this.updateMapData({
        devicesMapHolding: JSON.parse(JSON.stringify(devicesMapHolding)),
        vehicleStatusHolding: JSON.parse(JSON.stringify(vehicleStatusHolding))
      })

      // redraw the map view
      //window.EventBus.$emit('forceMapRedraw')
      // subscribe to updates for these specific vehicles

      if(this.connectionID) {
        if(this.hasSubscription) {
          await UnsubscribeVehicleUpdates(orgID, {
            connectionId: this.connectionID,
            context: "vehicle",
            subscriptionType: "presenceUpdates",
          }).catch(err => {
            logger.debug("delete subscription failed", err)
          })
        }
      }

      this.socketSubscriptions = [{
        name: 'presenceUpdates',
        subscribe: (sockId) => {
          SubscribeVehicleUpdates(orgID, {
            connectionId: sockId,
            subscriptionType: "presenceUpdates",
            context: "vehicle",
            values: vehicleIDList
          })
        }
      }]

      this.hasSubscription = true

      if(this.connectionID) {
        this.renewSubscriptions(this.connectionID)
      }
    })
  },

  destroyed () {
    logger.debug('web socket component destroyed')
    this.socket = null
    this.connectionID = null
    clearInterval(this.restartInterval)
  },

  methods: {
    ...mapMutations("devicePresence", [
      "updateMapData"
    ]),

    // initialise our web socket
    async initWS() {
      // we do not try to init a WS if our socket already exists
      if(this.socket) {
        logger.debug('web socket already exists, initialisation cancelled')
        return
      }

      logger.debug("trying to initialize web socket")
      try {
        const token = await getToken()
        this.connectionID = false
        this.socket = new Sockette(`${process.env.VUE_APP_PVWSS}?Auth=${token}`, {
          timeout: 10000,
          maxAttempts: 30,
          onopen: e => this.handleOpen(e),
          onmessage: e => this.handleMessage(e),
          onreconnect: e => this.handleReconnect(e),
          onmaximum: e => this.handleMaxExceeded(e),
          onclose: e => this.handleClose(e),
          onerror: e => this.handleClose(e, "error"),
        })
      } catch {
        logger.debug("could not initialise socket")
      }
    },

    // handler when websocket is opened
    async handleOpen(e) {
      logger.debug("websocket opened", e)

      if(this.socket) {
        try {
          this.socket.json({
            action: "getConnectionId"
          })
        } catch (error) {
          logger.debug("web socket could not be opened", error)
          this.restartFunc = () => {
            this.initWS()
          }
        } 
      }
    },

    // when we reconnect to the websocket, we need to forget our previous connection id
    async handleReconnect (e) {
      logger.debug("websocket reconnect", e)
      this.connectionID = false
    },

    // handler when the websocket is closed
    async handleClose (e, eventname = 'closed') {
      logger.debug(`websocket ${eventname}`, e)
      logger.debug(`websocket closed. restart function added`, e)
      this.socket = null
      this.connectionID = null
      this.restartFunc = () => {
        this.initWS()
      }
    },

    handleMaxExceeded (e) {
      logger.debug("maximum connection attempts exceeded", e)
      logger.debug("a websocket connection could not be established. restart function added")
      this.socket = null
      this.connectionID = null
      this.restartFunc = () => {
        this.initWS()
      }
    },

    handleMessage(e) {
      logger.debug("message recieved", e)

      let data = false
      try {
        data = JSON.parse(e.data)
      } catch {
        logger.debug("message could not be parsed", e)
      }
        
      if (!this.connectionID && !data) {
        // update the connection id in our store
        this.connectionID = e.data
        logger.debug("connection id", e.data)
        this.renewSubscriptions(e.data) 
        return false
      }

      // this should not happen, but in the event it does!
      if(!this.connectionID && data) {
        logger.debug("recieved a message but websocket id was not set", data, e)
      }

      const property = data.action ? data.action 
        : data.subscription_type ? data.subscription_type
        : false

      logger.debug(`${property} message recieved`, property, e)
      
      // if our websocket has a function defined for this action we execute it here.
      // our websocket functions are stored in an object in Vuex store. 
      if(property && typeof this.socketEventHandlers[property] == 'function') {
         this.socketEventHandlers[property](data)
      } else {
        logger.debug("missing websocket handler", `the action ${property} has no event handler`)
      }

      return false
    },

   // renews any subscriptions we have when the websocket is reconnected
    renewSubscriptions (newSocketID) {
      // run each subscription
      this.socketSubscriptions.forEach(sub => {
        sub.subscribe(newSocketID)
      })
    },

    // adds any default handlers we have for websocket messages
    addDefaulthandlers () {
      this.socketEventHandlers['presenceUpdates'] = this.handlePresenceUpdates
    },

    handlePresenceUpdates(response) {    
      try {
        const item = response.value
        if(this.devicesMap) {
          const { lat, long } = item.last_location.coords
          const device = {
            coor: { lat: getCoor(lat), lng: getCoor(long) },
            id: item.logical_id,
            vehicle_id: item.vehicle_id,
            fillColor: getColor(item.status),
            name: this.vehiclesSet[item.vehicle_id].vehicle_name,
            mileage: item.mileage,
            direction: isUnknown(item.last_location.course) ? 0 : parseInt(item.last_location.course),
            type: this.vehiclesSet[item.vehicle_id].vehicle_type.toLowerCase(),
            status: getStatus(item.status),
            statusText: item.status,
            speed: isUnknown(item.current_speed) ? item.current_speed : Number(item.current_speed),
            data: item,
            channels: ["CH1", "CH2", "CH3", "CH4"]
          }

          this.updateDevicePresence(device)
        }
      } catch (exception) {
        logger.debug("pres update failed", exception)
      }
    },
  }
}
</script>